import { Checkbox, Column, MultiSelectionButtonProps, TIcon } from '@acceso/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type Identificable = {
  id: string;
};

type InternalTableSelection<Item> = {
  all: boolean;
  selectedItems: Item[];
  unselectedItems: Item[];
};

type Action<Item> = {
  icon: TIcon;
  onClick: (selection: InternalTableSelection<Item>) => void;
  tooltip: string;
  subIcons?: TIcon[];
};

type UseTableMultiselectParams<Item> = {
  itemsCount: number;
  actions: Action<Item>[];
};

export function useTableMultiselect<Item extends Identificable>({
  itemsCount,
  actions,
}: UseTableMultiselectParams<Item>) {
  const { t } = useTranslation();
  const [showCheckboxColumn, setShowCheckboxColumn] = useState(false);
  const [selection, setSelection] = useState<InternalTableSelection<Item>>({
    all: false,
    selectedItems: [],
    unselectedItems: [],
  });

  const isIndeterminate =
    (selection.all && selection.unselectedItems.length > 0) ||
    (!selection.all && selection.selectedItems.length > 0);

  const toggleAll = () => {
    setSelection(prevSelection => ({
      all: !prevSelection.all,
      selectedItems: [],
      unselectedItems: [],
    }));
  };

  const toggleItem = (currentItem: Item) => {
    setSelection(prevSelection => {
      if (prevSelection.all) {
        const isUnselected = prevSelection.unselectedItems.some(
          unselectedItem => unselectedItem.id === currentItem.id
        );

        const unselectedItemsWithoutCurrent = prevSelection.unselectedItems.filter(
          unselectedItem => unselectedItem.id !== currentItem.id
        );
        const unselectedItemsWithCurrent = [...prevSelection.unselectedItems, currentItem];

        const updatedUnselectedItems = isUnselected
          ? unselectedItemsWithoutCurrent
          : unselectedItemsWithCurrent;

        return {
          ...prevSelection,
          unselectedItems: updatedUnselectedItems,
        };
      } else {
        const isSelected = prevSelection.selectedItems.some(
          selectedItem => selectedItem.id === currentItem.id
        );

        const selectedItemsWithoutCurrent = prevSelection.selectedItems.filter(
          selectedItem => selectedItem.id !== currentItem.id
        );
        const selectedItemsWithCurrent = [...prevSelection.selectedItems, currentItem];

        const updatedSelectedItems = isSelected
          ? selectedItemsWithoutCurrent
          : selectedItemsWithCurrent;

        return {
          ...prevSelection,
          selectedItems: updatedSelectedItems,
        };
      }
    });
  };

  const checkboxColumn: Column<Item> = {
    id: 'check',
    width: '5%',
    isVisible: showCheckboxColumn,
    title: (
      <div className="flex h-full items-center">
        <Checkbox
          checked={selection.all && selection.unselectedItems.length === 0}
          indeterminate={isIndeterminate}
          onChange={toggleAll}
        />
      </div>
    ),
    render: currentItem => {
      const isCheckedWhenAllSelected = !selection.unselectedItems.some(
        unselectedItem => unselectedItem.id === currentItem.id
      );
      const isCheckedWhenNotAllSelected = selection.selectedItems.some(
        selectedItem => selectedItem.id === currentItem.id
      );
      const isChecked = selection.all ? isCheckedWhenAllSelected : isCheckedWhenNotAllSelected;

      return (
        <Checkbox
          checked={isChecked}
          onChange={() => {
            toggleItem(currentItem);
          }}
        />
      );
    },
  };

  const selectedCount = selection.all
    ? itemsCount - selection.unselectedItems.length
    : selection.selectedItems.length;

  const counterProps = {
    selected: selectedCount,
    total: itemsCount,
  };

  const mappedActions = actions.map(action => ({
    ...action,
    onClick: () => {
      action.onClick(selection);
    },
    disabled: selectedCount === 0,
  }));

  const multiSelectionButtonProps: MultiSelectionButtonProps = {
    text: t('Multiselección'),
    counter: counterProps,
    actions: mappedActions,
    onChangeShow: setShowCheckboxColumn,
    numbersWithInternationalFormat: false,
  };

  return {
    checkboxColumn,
    multiSelectionButtonProps,
  };
}
